// Leak detection thresholds (from detect_leaks.py)
const FALCON_MEAS_THRESHOLD = 500
const FALCON_1F_THRESHOLD = 30
const FALCON_2F_THRESHOLD = 100
const FALCON_STATE_OK = new Set(['0', '1'])
const CLUSTER_RADIUS_METERS = 30

// Haversine distance calculation
const haversineDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371000 // Earth's radius in meters
  
  const toRadians = (degrees) => degrees * (Math.PI / 180)
  
  const dLat = toRadians(lat2 - lat1)
  const dLon = toRadians(lon2 - lon1)
  
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) *
    Math.sin(dLon/2) * Math.sin(dLon/2)
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a))
  
  return R * c
}

// Cluster nearby leaks
const clusterLeaks = (leaks) => {
  if (leaks.length === 0) return []
  
  const clusters = []
  const used = new Set()
  
  for (let i = 0; i < leaks.length; i++) {
    if (used.has(i)) continue
    
    // Start a new cluster with this leak
    const cluster = [leaks[i]]
    used.add(i)
    
    // Find all nearby leaks
    for (let j = 0; j < leaks.length; j++) {
      if (used.has(j)) continue
      
      const distance = haversineDistance(
        leaks[i].lat, leaks[i].lng,
        leaks[j].lat, leaks[j].lng
      )
      
      if (distance <= CLUSTER_RADIUS_METERS) {
        cluster.push(leaks[j])
        used.add(j)
      }
    }
    
    // Select the leak with highest FALCON_MEAS from this cluster
    const bestLeak = cluster.reduce((best, current) => 
      current.falconMeas > best.falconMeas ? current : best
    )
    
    clusters.push({
      ...bestLeak,
      clusterSize: cluster.length,
      clusterMembers: cluster
    })
  }
  
  return clusters
}

// Main leak detection function
export const detectLeaks = (data) => {
  const leaks = []
  
  for (const point of data) {
    // Primary validation: FALCON_MEAS and FALCON_1F thresholds
    if (point.falconMeas > FALCON_MEAS_THRESHOLD &&
        FALCON_STATE_OK.has(point.falconState) &&
        point.falcon1F > FALCON_1F_THRESHOLD &&
        point.lat !== 0 && point.lng !== 0) {
      
      // Secondary validation: FALCON_2F threshold
      // If FALCON_MEAS is borderline (150-200), require FALCON_2F validation
      if (point.falconMeas <= 200) {
        if (point.falcon2F > FALCON_2F_THRESHOLD) {
          leaks.push(point)
        }
      } else {
        // For higher FALCON_MEAS values, FALCON_2F is optional but should be reasonable
        if (point.falcon2F > 0) { // Must have some FALCON_2F value
          leaks.push(point)
        }
      }
    }
  }
  
  // Cluster the leaks and select highest from each cluster
  return clusterLeaks(leaks)
}

// Get data quality color for visualization
export const getDataQualityColor = (point) => {
  // Check FALCON_STATE first
  if (point.falconState === '5') return '#ef4444' // red - low reflection
  if (point.falconState === '6') return '#f59e0b' // amber - saturation
  if (point.falconState === '7') return '#f59e0b' // amber - sunlight interference
  if (point.falconState === '0') return '#6b7280' // gray - off
  
  // For good state (1), check FALCON_1F quality
  if (point.falcon1F < 10) return '#ef4444' // red - poor reflection
  if (point.falcon1F < 30) return '#f59e0b' // amber - low reflection
  if (point.falcon1F > 200) return '#f59e0b' // amber - possible saturation
  
  return '#22c55e' // green - good quality
}

// Get leak severity color
export const getLeakSeverityColor = (falconMeas) => {
  if (falconMeas > 2000) return '#dc2626' // red-600 - very high
  if (falconMeas > 1000) return '#ea580c' // orange-600 - high
  if (falconMeas > 500) return '#d97706' // amber-600 - medium
  return '#16a34a' // green-600 - low
}
